import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getInsightsAwardedRedeemed } from "../../actions/acc_actions";
import Loading from "../../utils/Loading";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
);

const RewardsChart = ({ dateFrom, dateTo, selectedSchema }) => {
  const { t } = useTranslation();

  const businessSlug = useSelector((state) => state.account.businessSlug);

  const { isLoading, data: AwardedRedeemedData } = useQuery({
    queryKey: [
      "insightsAwardedRedeemed",
      businessSlug,
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getInsightsAwardedRedeemed({
        to: dateTo,
        from: dateFrom,
        refresh: false,
        rewardType: "all",
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!businessSlug && !!selectedSchema,
    keepPreviousData: true,
  });

  if (isLoading) return <Loading />;

  const chartData = AwardedRedeemedData?.body?.data || [];

  if (!chartData.length) return <div />;

  // -----------------------
  // CSV Export
  // -----------------------
  const handleDownloadCSV = () => {
    const headers = ["Date", "Vouchers Awarded", "Rewards Redeemed"];

    const rows = chartData.map((item) => [
      item.date,
      item.vouchers_awarded,
      item.rewards_redeemed,
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = `rewards-vouchers-${dateFrom}-to-${dateTo}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  const chartConfig = {
    labels: chartData.map((item) => item.date),
    datasets: [
      {
        label: t("vouchers_awarded"),
        data: chartData.map((item) => item.vouchers_awarded),
        borderColor: "#1890ff",
        backgroundColor: "rgba(24,144,255,.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("rewards_redeemed"),
        data: chartData.map((item) => item.rewards_redeemed),
        borderColor: "#52c41a",
        backgroundColor: "rgba(82,196,26,.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: {
            day: "MMM dd",
          },
        },
        title: {
          display: true,
          text: t("date"),
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("count"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
  };

  return (
    <div className="chart">
      <h3>{t("rewards_vouchers_chart_title")}</h3>

      <div className="chart-wrapper">
        <div className="chart-item">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h4
              style={{
                margin: 0,
              }}
            >
              {t("vouchers_vs_rewards")}
            </h4>

            <Button icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
              {t("download_data")}
            </Button>
          </div>

          <div className="chart-container">
            <Line data={chartConfig} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsChart;
